import * as v from "valibot";

const PASSWORD_MIN_LENGTH = 10;

export default function useSchemas() {
    const { t } = useI18n();

    const BomNameSchema = v.pipe(v.string(), v.trim(), v.nonEmpty(t("BOMTool.BOMNameRequired")));

    const BomQuantitySchema = v.pipe(
        v.string(),
        v.decimal("A number is required"),
        v.transform(Number),
        v.transform(Math.floor),
        v.minValue(1, "Value must be 1 or more")
    );

    const EmailSchema = v.pipe(v.string(), v.trim(), v.email(t("Global.EmailRequired")));

    const LoginSchema = v.object({
        UserName: v.pipe(v.string(), v.trim(), v.nonEmpty(t("SignIn.UserNameRequiredErrorMessage"))),
        Password: v.pipe(v.string(), v.trim(), v.nonEmpty(t("Global.PasswordRequired"))),
    });

    const PasswordSchema = v.pipe(
        v.string(),
        v.nonEmpty(t("Global.PasswordRequired")),
        v.minLength(
            PASSWORD_MIN_LENGTH,
            t("Register.PasswordStrengthCharactersError", [formatNumber(PASSWORD_MIN_LENGTH)])
        )
    );

    const ConfirmPasswordSchema = v.pipe(v.string(), v.nonEmpty(t("Register.ConfirmPasswordError")));

    const RegisterSchema = v.pipe(
        v.object({
            UserName: v.pipe(
                v.string(),
                v.trim(),
                v.nonEmpty(t("Register.UserNameError")),
                v.minLength(6, t("Register.UsernameMinLength")),
                v.regex(/^[a-zA-Z0-9_.-]+$/, t("Register.ValidUsernameError"))
            ),
            Name: v.pipe(v.string(), v.trim(), v.nonEmpty(t("Register.NameError"))),
            CompanyName: v.optional(v.pipe(v.string(), v.trim())),
            Email: v.pipe(
                v.string(),
                v.trim(),
                v.nonEmpty(t("Register.EmailError")),
                v.email(t("Register.ValidEmailError"))
            ),
            Password: PasswordSchema,
            ConfirmPassword: ConfirmPasswordSchema,
            TermsAndConditions: v.literal(true, t("Register.TermsConditionsError")),
        }),
        v.forward(
            v.partialCheck(
                [["Password"], ["ConfirmPassword"]],
                (input) => input.Password === input.ConfirmPassword,
                t("Register.PasswordMatchError")
            ),
            ["ConfirmPassword"]
        )
    );

    const ForgotPasswordSchema = v.object({
        Email: v.pipe(
            v.string(),
            v.trim(),
            v.nonEmpty(t("Register.EmailError")),
            v.email(t("Register.ValidEmailError"))
        ),
    });

    const ChangePasswordSchema = v.pipe(
        v.object({
            OldPassword: v.pipe(v.string(), v.nonEmpty(t("Change_Password.OldPasswordRequired"))),
            NewPassword: PasswordSchema,
            ConfirmPassword: ConfirmPasswordSchema,
        }),
        v.forward(
            v.partialCheck(
                [["NewPassword"], ["ConfirmPassword"]],
                (input) => input.NewPassword === input.ConfirmPassword,
                t("Register.PasswordMatchError")
            ),
            ["ConfirmPassword"]
        )
    );

    const ResetPasswordSchema = v.pipe(
        v.object({
            NewPassword: PasswordSchema,
            ConfirmPassword: ConfirmPasswordSchema,
        }),
        v.forward(
            v.partialCheck(
                [["NewPassword"], ["ConfirmPassword"]],
                (input) => input.NewPassword === input.ConfirmPassword,
                t("Register.PasswordMatchError")
            ),
            ["ConfirmPassword"]
        )
    );

    const TotpCodeSchema = v.object({
        CodeDigits: v.pipe(
            v.array(v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(9))),
            v.length(6, t("Security.InvalidVerificationCode"))
        ),
    });

    return {
        BomNameSchema,
        BomQuantitySchema,
        ChangePasswordSchema,
        EmailSchema,
        ForgotPasswordSchema,
        LoginSchema,
        RegisterSchema,
        ResetPasswordSchema,
        TotpCodeSchema,
    };
}
